import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateAllergyInput,
  UpdateAllergyInput,
} from "@shared/validation/allergy.schema";
import type { Severity } from "@shared/constants/severity";
import { apiClient, ApiError } from "./client";

export interface AllergySummary {
  id: string;
  childId: string;
  name: string;
  severity: Severity;
  description: string | null;
  emergencyAction: string | null;
  reactionSymptoms: string | null;
  treatment: string | null;
  epinephrineLocation: string | null;
  rescueMedicationId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const allergiesKey = (childId: string) => ["children", childId, "allergies"] as const;

export function useAllergies(childId: string | undefined) {
  return useQuery<AllergySummary[], ApiError>({
    queryKey: childId ? allergiesKey(childId) : ["children", "__none__", "allergies"],
    queryFn: () => apiClient<AllergySummary[]>(`/api/children/${childId}/allergies`),
    enabled: Boolean(childId),
  });
}

export function useCreateAllergy(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<AllergySummary, ApiError, CreateAllergyInput>({
    mutationFn: (vars) =>
      apiClient<AllergySummary>(`/api/children/${childId}/allergies`, {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allergiesKey(childId) });
    },
  });
}

export function useUpdateAllergy(childId: string, allergyId: string) {
  const queryClient = useQueryClient();
  return useMutation<AllergySummary, ApiError, UpdateAllergyInput>({
    mutationFn: (vars) =>
      apiClient<AllergySummary>(`/api/children/${childId}/allergies/${allergyId}`, {
        method: "PUT",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allergiesKey(childId) });
    },
  });
}

export function useDeleteAllergy(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ deleted: true }, ApiError, string>({
    mutationFn: (allergyId) =>
      apiClient<{ deleted: true }>(`/api/children/${childId}/allergies/${allergyId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allergiesKey(childId) });
    },
  });
}
